import { useEffect, useRef } from 'react'
import { emit } from '../../platform/analytics/analytics'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import { activeFilterCount, type TxnFilters } from './filters'
import type { TxnPage } from './useTransactions'

/** The kinds `transaction_filter_applied` may carry: a word for WHICH filter, never its value. */
export type FilterKind = (typeof ENUM_VALUES.filter_kind)[number]

/**
 * The filters a write APPLIED: those that changed and now hold a value. Clearing one is not an
 * application, and neither are search text (its own event) or the page.
 */
export function appliedFilterKinds(prev: TxnFilters, next: TxnFilters): FilterKind[] {
  const kinds: FilterKind[] = []
  if (next.month !== prev.month) kinds.push('month')
  if (next.accountId !== prev.accountId && next.accountId !== null) kinds.push('account')
  if (next.categoryId !== prev.categoryId && next.categoryId !== null) kinds.push('category')
  if (next.ownerId !== prev.ownerId && next.ownerId !== null) kinds.push('owner')
  if (next.tag !== prev.tag && next.tag !== null) kinds.push('tag')
  if ((next.spentMin !== prev.spentMin || next.spentMax !== prev.spentMax) && (next.spentMin !== null || next.spentMax !== null)) {
    kinds.push('amount')
  }
  if (next.pending !== prev.pending && next.pending !== null) kinds.push('pending')
  if (next.review !== prev.review && next.review) kinds.push('review')
  return kinds
}

export function emitFiltersApplied(prev: TxnFilters, next: TxnFilters): void {
  for (const filter_kind of appliedFilterKinds(prev, next)) emit('transaction_filter_applied', { filter_kind })
}

/**
 * `transactions_viewed` once per mount, on the first success, and `transaction_search` once per
 * search the USER applied once its result is in. A search already in the URL on arrival is not
 * announced: nobody searched. Only lengths and counts leave: never the text.
 */
export function useListAnalytics(
  list: { status: string; data?: TxnPage },
  filters: TxnFilters,
  needsReviewCount: number | undefined,
): void {
  const viewed = useRef(false)
  const searched = useRef(filters.q.trim())
  const total = list.status === 'success' ? list.data?.total : undefined
  const query = filters.q.trim()
  const filterCount = activeFilterCount(filters)
  useEffect(() => {
    if (total === undefined || viewed.current) return
    viewed.current = true
    emit('transactions_viewed', { total_count: total, needs_review_count: needsReviewCount, filter_count: filterCount })
  }, [total, needsReviewCount, filterCount])
  useEffect(() => {
    if (total === undefined || query === searched.current) return
    searched.current = query
    if (query !== '') emit('transaction_search', { query_length: query.length, result_count: total })
  }, [total, query])
}
